import React, { useState } from 'react';
import { XCircle, GitBranch, MoreHorizontal } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import BulkStageChangeModal from './BulkStageChangeModal';

/**
 * BulkActionToolbar - Actions bar that appears when clients are selected.
 *
 * Sprint 292 — the destructive bulk action is now "Close Accounts" (calls
 * the S4 lifecycle endpoint per selection), NOT a raw Firestore
 * `batch.delete`. Hard purge is not exposed here at all — it is an
 * owner-only backend workflow that must be executed one contact at a
 * time via `POST /api/leads/{leadId}/purge`.
 */
const BulkActionToolbar = ({
  selectedCount,
  onBulkCloseAccounts,
  onBulkStageChange,
  availableStages,
  isProcessing,
}) => {
  const [showStageModal, setShowStageModal] = useState(false);

  if (selectedCount === 0) return null;

  return (
    <>
      <div className="bg-blue-50 border-b border-blue-200 px-4 py-3 transition-all">
        <div className="flex items-center justify-between">
          {/* Left: Selection count */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-blue-900">
              {selectedCount} client{selectedCount > 1 ? 's' : ''} selected
            </span>
          </div>

          {/* Right: Action buttons */}
          <div className="flex items-center gap-2">
            {/* Change Stage */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowStageModal(true)}
              disabled={isProcessing}
              className="text-blue-700 border-blue-300 hover:bg-blue-100"
              data-testid="bulk-change-stage-btn"
            >
              <GitBranch className="h-4 w-4 mr-2" />
              Change Stage
            </Button>

            {/* Close Accounts — replaces the former "Delete Selected". Uses
                the S4 per-lead lifecycle endpoint, preserving history and
                cancelling future recurring work. Non-destructive. */}
            <Button
              variant="outline"
              size="sm"
              onClick={onBulkCloseAccounts}
              disabled={isProcessing || !onBulkCloseAccounts}
              className="text-amber-700 border-amber-300 hover:bg-amber-50"
              data-testid="bulk-close-accounts-btn"
            >
              <XCircle className="h-4 w-4 mr-2" />
              Close Accounts
            </Button>

            {/* More actions (placeholder for future) */}
            <Button
              variant="outline"
              size="sm"
              disabled
              className="text-gray-500 border-gray-300"
              title="More actions coming soon"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Stage Change Modal */}
      <BulkStageChangeModal
        isOpen={showStageModal}
        onClose={() => setShowStageModal(false)}
        onConfirm={onBulkStageChange}
        selectedCount={selectedCount}
        availableStages={availableStages}
      />
    </>
  );
};

export default BulkActionToolbar;
